/**
 * Projects: every project the signed-in account is a member of, one row
 * each, filterable by `?q=`.
 *
 * The rows are `/auth/session`'s memberships (`project-directory.ts`); an
 * authority served without the site lists its one project. One project is
 * read at a time, so only the row of the project in hand carries readings;
 * every other row says it has not been read rather than borrowing figures.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

import type { ChangeRow, SurfaceSnapshot } from '../generated/surface.js';
import { PROJECTS_PATH, projectHref } from './nav.js';
import { projectEntries, type ProjectDirectory } from './project-directory.js';
import {
  ago,
  authorityState,
  authorityStatusBadge,
  count,
  Gap,
  GAP_DASH,
  percent,
  producerIdentity,
  ProductSpark,
  statusTone,
  projected,
  projectName,
  reading,
} from './product.js';
import { CliCommands, KitBadge, KitTable, PageHeader, RowCard, Section } from './kit.js';
import type { SurfaceConnectionState } from './surface-connection.js';

const DAY_S = 86_400;

/*
 * The observed series, or nothing.
 *
 * With no timestamps this once returned one bar per change at uniform height,
 * and ProductSpark drew it as stored data. No authority stamps
 * `observed_at_unix` yet and the authority separately declares
 * `throughput_series` absent, so the absence is what renders.
 */
function observedVelocity(changes: readonly ChangeRow[]): number[] | null {
  const timed = changes.flatMap((change) => {
    const observed = reading(change.observed_at_unix);
    return observed === undefined ? [] : [observed];
  });
  if (timed.length === 0) return null;
  const latest = Math.max(...timed);
  const buckets = [0, 0, 0, 0, 0, 0, 0];
  for (const observed of timed) {
    const daysAgo = Math.min(6, Math.floor((latest - observed) / DAY_S));
    const index = 6 - daysAgo;
    buckets[index] = (buckets[index] ?? 0) + 1;
  }
  return buckets;
}

function meanComposition(snapshot: SurfaceSnapshot): string | null {
  const durations = (projected(snapshot, 'fabric_runs') ?? []).flatMap((run) => {
    const measurement = reading(run.duration_ms);
    return measurement === undefined ? [] : [measurement];
  });
  if (durations.length === 0) return null;
  const average = Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length);
  if (average < 1000) return `${count(average)}ms`;
  return `${(average / 1000).toFixed(1)}s`;
}

/** The figures one project's snapshot supports. */
type ProjectReading = {
  snapshot: SurfaceSnapshot;
  certifiedRate: string | TemplateInstance;
  velocity: number[] | null;
  composition: string | null;
  producers: string[];
};

type ProjectRow = {
  name: string;
  /** The membership's role; null where the origin names no membership. */
  role: string | null;
  href: string;
  reading: ProjectReading | null;
};

function projectReading(snapshot: SurfaceSnapshot): ProjectReading {
  const accepted = new Set(snapshot.certificates.flatMap((certificate) => certificate.changes));
  /*
   * A rate needs both operands from the same population. The denominator is
   * the served window, so where the authority declares that window it is not
   * a population this surface holds, and the rate is its declaration.
   */
  const changeWindow = projected(snapshot, 'recent_changes');
  const certifiedRate = changeWindow
    ? changeWindow.length === 0
      ? GAP_DASH
      : percent(accepted.size, changeWindow.length)
    : Gap(snapshot, 'recent_changes');
  return {
    snapshot,
    certifiedRate,
    velocity: observedVelocity(snapshot.recent_changes),
    composition: meanComposition(snapshot),
    producers: [...new Set(snapshot.recent_changes.map((change) => change.producer))],
  };
}

/** The listed projects, each linked to its own address. */
function projectRows(snapshot: SurfaceSnapshot | null, directory: ProjectDirectory): ProjectRow[] {
  return projectEntries(directory, snapshot).map((entry) => ({
    name: entry.role === null && entry.reading ? projectName(entry.reading) : entry.name,
    role: entry.role,
    href: projectHref('overview', {}, entry.scope),
    reading: entry.reading ? projectReading(entry.reading) : null,
  }));
}

/** A cell of a project this page holds no projection for. */
export const NOT_READ = 'Not read';

export function NotRead(): TemplateInstance {
  return html`<small title="Open the project to read it">${NOT_READ}</small>`;
}

/** `?q=` from the address, trimmed; empty means no filter. */
export function requestedProjectQuery(search: string): string {
  return (new URLSearchParams(search).get('q') ?? '').trim();
}

function SearchBox(query: string): TemplateInstance {
  return html`<form class="mx-project-search" role="search" method="get" action="${PROJECTS_PATH}">
    <label class="mx-visually-hidden" for="mx-project-search">Filter projects</label>
    <input id="mx-project-search" class="ui-input" data-slot="input" type="search" name="q" value="${query}" placeholder="Filter projects" autocomplete="off" />
  </form>`;
}

/** What an account that is a member of no project is told, wherever a project would be. */
export const NO_PROJECTS =
  'Your account is not a member of any project yet. Accepting an invite to a project adds it here.';

/** A project is imported by the CLI from a Git mirror; the web app only reads what the import certified. */
export function ImportFromGitHub(): TemplateInstance {
  return CliCommands({
    label: 'Import from GitHub',
    detail: 'Importing is a CLI act; this page shows the project once its certified state is served:',
    commands: ['matrix import github --git-dir <bare-mirror> --project <owner/repo> --authority <url>'],
  });
}

function StatusCell(state: ReturnType<typeof authorityState>): TemplateInstance {
  const label = authorityStatusBadge(state);
  return KitBadge(label, statusTone(label));
}

export function ConsumerProjects(
  snapshot: SurfaceSnapshot | null,
  connection: SurfaceConnectionState,
  directory: ProjectDirectory = null,
): TemplateInstance {
  const search = typeof globalThis.location === 'undefined' ? '' : (globalThis.location.search ?? '');
  const query = requestedProjectQuery(search);
  const all = projectRows(snapshot, directory);
  const needle = query.toLowerCase();
  const rows = needle.length === 0 ? all : all.filter((row) => row.name.toLowerCase().includes(needle));
  const state = authorityState(snapshot, connection);
  const noun = all.length === 1 ? 'project' : 'projects';
  const heading = query.length === 0 ? `${count(all.length)} ${noun}` : `${count(rows.length)} of ${count(all.length)} ${noun} matching “${query}”`;
  const members = directory !== null;

  return html`
    <section class="mx-page" data-region="consumer-projects">
      ${PageHeader({
        title: 'Projects',
        description: members
          ? 'The Matrix-governed projects your account is a member of, with live certification evidence for the one open now.'
          : 'Matrix-governed projects this authority serves, with their live certification evidence.',
        actions: SearchBox(query),
      })}
      ${Section({
        title: heading,
        region: 'projects-catalogue',
        children: KitTable({
          label: 'Projects',
          rows,
          rowKey: (row) => row.href,
          empty: query.length > 0 ? `No project matches “${query}”.` : members ? NO_PROJECTS : 'No project is stored in this authority.',
          card: (row) =>
            RowCard({
              title: html`<a class="mx-cell-link" href="${row.href}" data-nav="${row.href}">${row.name}</a>`,
              description: row.role === null ? 'Served by this authority' : `Your role: ${row.role}`,
              action: row.reading ? StatusCell(state) : NotRead(),
              facts: row.reading
                ? [
                    { label: 'Stored changes', value: html`<strong>${count(row.reading.snapshot.counts.changes)}</strong>` },
                    { label: 'Certification rate', value: row.reading.certifiedRate ?? NotRead() },
                    { label: 'Mean composition time', value: row.reading.composition ?? Gap(row.reading.snapshot, 'fabric_run_duration') },
                    { label: 'Producers', value: count(row.reading.producers.length) },
                    { label: 'Last updated', value: ago(row.reading.snapshot, row.reading.snapshot.clock.last_observed_unix) },
                  ]
                : [],
            }),
          columns: [
            {
              label: 'Project',
              render: (row) => html`<a class="mx-cell-link" href="${row.href}" data-nav="${row.href}"><strong>${row.name}</strong><small>${row.role === null ? 'Served by this authority' : `Your role: ${row.role}`}</small></a>`,
            },
            { label: 'Status', render: (row) => (row.reading ? StatusCell(state) : NotRead()) },
            { label: 'Stored changes', align: 'end', render: (row) => (row.reading ? html`<strong>${count(row.reading.snapshot.counts.changes)}</strong>${row.reading.velocity ? ProductSpark(row.reading.velocity) : Gap(row.reading.snapshot, 'throughput_series')}` : NotRead()) },
            { label: 'Certification rate', align: 'end', render: (row) => row.reading?.certifiedRate ?? NotRead() },
            { label: 'Mean composition time', align: 'end', render: (row) => (row.reading ? (row.reading.composition ?? Gap(row.reading.snapshot, 'fabric_run_duration')) : NotRead()) },
            {
              label: 'Producers',
              render: (row) =>
                row.reading
                  ? html`<span class="mx-avatars">${repeat(row.reading.producers.slice(0, 3), (id) => id, (id) => html`<span class="ui-avatar" data-slot="avatar" data-size="sm" title="${id}"><span class="ui-avatar-fallback" data-slot="avatar-fallback">${producerIdentity(id).initials}</span></span>`)}<small>${count(row.reading.producers.length)}</small></span>`
                  : NotRead(),
            },
            { label: 'Last updated', align: 'end', render: (row) => (row.reading ? ago(row.reading.snapshot, row.reading.snapshot.clock.last_observed_unix) : NotRead()) },
          ],
        }),
      })}
    </section>
  `;
}

/**
 * Every page that shows a project, for an account that is a member of none.
 * Nothing is read for it: the Worker refuses each `/v1/` read `no_project`,
 * so the page says why there is nothing rather than waiting on a refusal.
 */
export function NoProjects(title: string): TemplateInstance {
  return html`
    <section class="mx-page" data-region="no-projects">
      ${PageHeader({ title, description: 'Matrix shows the projects your account is a member of.' })}
      <div class="ui-empty" data-slot="empty">
        <div class="ui-empty-header" data-slot="empty-header">
          <h2 class="ui-empty-title" data-slot="empty-title">No projects yet</h2>
          <p class="ui-empty-description" data-slot="empty-description">${NO_PROJECTS}</p>
        </div>
        <div class="ui-empty-content" data-slot="empty-content">${ImportFromGitHub()}</div>
      </div>
    </section>
  `;
}
